import { baseApi } from '@/api/core/baseApi';
import { unwrapApiResponse, unwrapListResponse } from '@/api/patterns/crud';
import type {
  PlatformGuardrail,
  PlatformGuardrailsResponse,
  GetGuardrailsArgs,
  PlatformGuardrailRequest,
  ApproveGuardrailRequestArgs,
  RejectGuardrailRequestArgs,
} from '../types';

export const platformGuardrailsApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPlatformGuardrails: builder.query<PlatformGuardrailsResponse, GetGuardrailsArgs | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.search) params.append('search', args.search);
        if (args?.severity) params.append('severity', args.severity);
        if (args?.category) params.append('category', args.category);
        if (args?.isActive) params.append('isActive', args.isActive);
        return `/platform/guardrails?${params.toString()}`;
      },
      transformResponse: unwrapApiResponse<PlatformGuardrailsResponse>,
      providesTags: [{ type: 'Guardrails', id: 'PLATFORM-LIST' }],
    }),

    createPlatformGuardrail: builder.mutation<PlatformGuardrail, Partial<PlatformGuardrail>>({
      query: (data) => ({
        url: '/platform/guardrails',
        method: 'POST',
        body: data,
      }),
      transformResponse: unwrapApiResponse<PlatformGuardrail>,
      invalidatesTags: [{ type: 'Guardrails', id: 'PLATFORM-LIST' }],
    }),

    updatePlatformGuardrail: builder.mutation<PlatformGuardrail, { id: string; data: Partial<PlatformGuardrail> }>({
      query: ({ id, data }) => ({
        url: `/platform/guardrails/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: unwrapApiResponse<PlatformGuardrail>,
      invalidatesTags: [{ type: 'Guardrails', id: 'PLATFORM-LIST' }],
    }),

    deletePlatformGuardrail: builder.mutation<void, string>({
      query: (id) => ({
        url: `/platform/guardrails/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Guardrails', id: 'PLATFORM-LIST' }],
    }),

    togglePlatformGuardrail: builder.mutation<PlatformGuardrail, string>({
      query: (id) => ({
        url: `/platform/guardrails/${id}/toggle`,
        method: 'POST',
      }),
      transformResponse: unwrapApiResponse<PlatformGuardrail>,
      invalidatesTags: [{ type: 'Guardrails', id: 'PLATFORM-LIST' }],
    }),

    // Guardrail requests
    getPlatformGuardrailRequests: builder.query<PlatformGuardrailRequest[], void>({
      query: () => '/platform/guardrail-requests',
      transformResponse: unwrapListResponse<PlatformGuardrailRequest>,
      providesTags: [{ type: 'Guardrails', id: 'REQUESTS-LIST' }],
    }),

    approveGuardrailRequest: builder.mutation<unknown, ApproveGuardrailRequestArgs>({
      query: ({ id, ...body }) => ({
        url: `/platform/guardrail-requests/${id}/approve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Guardrails', id: 'REQUESTS-LIST' },
        { type: 'Guardrails', id: 'REQUESTS-COUNT' },
        { type: 'Guardrails', id: 'PLATFORM-LIST' },
      ],
    }),

    rejectGuardrailRequest: builder.mutation<unknown, RejectGuardrailRequestArgs>({
      query: ({ id, adminNotes }) => ({
        url: `/platform/guardrail-requests/${id}/reject`,
        method: 'POST',
        body: { adminNotes },
      }),
      invalidatesTags: [
        { type: 'Guardrails', id: 'REQUESTS-LIST' },
        { type: 'Guardrails', id: 'REQUESTS-COUNT' },
      ],
    }),

    getGuardrailRequestsCount: builder.query<number, void>({
      query: () => '/platform/guardrail-requests/count',
      transformResponse: (response: { data: { count: number } } | { count: number }) => {
        if ('data' in response && response.data) return response.data.count;
        if ('count' in response) return response.count;
        return 0;
      },
      providesTags: [{ type: 'Guardrails', id: 'REQUESTS-COUNT' }],
    }),
  }),
});

export const {
  useGetPlatformGuardrailsQuery,
  useCreatePlatformGuardrailMutation,
  useUpdatePlatformGuardrailMutation,
  useDeletePlatformGuardrailMutation,
  useTogglePlatformGuardrailMutation,
  useGetPlatformGuardrailRequestsQuery,
  useApproveGuardrailRequestMutation,
  useRejectGuardrailRequestMutation,
  useGetGuardrailRequestsCountQuery,
} = platformGuardrailsApi;
